import { useState, useEffect } from 'react'
import { AppState, ModuleId } from '../types'
import { ALL_QUESTIONS_CORE, MODULES } from '../data/questionLoader'
import {
  updateQuestionState,
  calculateMasteryPercentage,
  getModuleStats,
} from '../utils/knowledgeStateEngine'

const STORAGE_KEY = 'cito_meester_djayden_v2'
const XP_PER_LEVEL = 250 // UPDATED: Was 100, nu 250

const INITIAL_STATE: AppState = {
  stats: {
    name: 'Djayden',
    xp: 0,
    level: 1,
    confidenceIndex: 0,
  },
  progress: {
    Metriek: {
      masteryPercentage: 0,
      status: 'learning',
      cheatSheetViewed: false,
      questionsAnswered: [],
      recentQuestions: [],
      reviewNeededCount: 0,
      uncertainCount: 0,
      overconfidentCount: 0,
    },
    Getalbegrip: {
      masteryPercentage: 0,
      status: 'learning',
      cheatSheetViewed: false,
      questionsAnswered: [],
      recentQuestions: [],
      reviewNeededCount: 0,
      uncertainCount: 0,
      overconfidentCount: 0,
    },
    TijdGeld: {
      masteryPercentage: 0,
      status: 'learning',
      cheatSheetViewed: false,
      questionsAnswered: [],
      recentQuestions: [],
      reviewNeededCount: 0,
      uncertainCount: 0,
      overconfidentCount: 0,
    },
    SpellingWW: {
      masteryPercentage: 0,
      status: 'learning',
      cheatSheetViewed: false,
      questionsAnswered: [],
      recentQuestions: [],
      reviewNeededCount: 0,
      uncertainCount: 0,
      overconfidentCount: 0,
    },
    SpellingRegel: {
      masteryPercentage: 0,
      status: 'learning',
      cheatSheetViewed: false,
      questionsAnswered: [],
      recentQuestions: [],
      reviewNeededCount: 0,
      uncertainCount: 0,
      overconfidentCount: 0,
    },
    Taalbegrip: {
      masteryPercentage: 0,
      status: 'learning',
      cheatSheetViewed: false,
      questionsAnswered: [],
      recentQuestions: [],
      reviewNeededCount: 0,
      uncertainCount: 0,
      overconfidentCount: 0,
    },
  },
  errorLog: [],
  questionStates: {},
  recentConfidenceScores: [],
  pendingLevelUp: undefined,
}

export function useAppStore() {
  const [state, setState] = useState<AppState>(INITIAL_STATE)
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY)

    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        if (parsed.stats && parsed.progress) {
          Object.keys(parsed.progress).forEach((moduleId) => {
            if (parsed.progress[moduleId].status === 'locked') {
              parsed.progress[moduleId].status = 'learning'
            }
          })

          if (!parsed.questionStates) parsed.questionStates = {}
          if (!parsed.recentConfidenceScores) parsed.recentConfidenceScores = []
          if (!parsed.stats.confidenceIndex) parsed.stats.confidenceIndex = 0
          if (!parsed.pendingLevelUp) parsed.pendingLevelUp = undefined

          setState(parsed)
          console.log('✅ State loaded from localStorage:', {
            totalQuestions: Object.keys(parsed.questionStates).length,
            xp: parsed.stats.xp,
            level: parsed.stats.level,
          })
        }
      } catch (e) {
        console.error('❌ Failed to parse stored state', e)
      }
    }
    setIsInitialized(true)
  }, [])

  useEffect(() => {
    if (isInitialized) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
      } catch (e) {
        console.error('❌ Failed to save state to localStorage', e)
      }
    }
  }, [state, isInitialized])

  const markCheatSheetViewed = (moduleId: ModuleId) => {
    setState((prev) => ({
      ...prev,
      progress: {
        ...prev.progress,
        [moduleId]: { ...prev.progress[moduleId], cheatSheetViewed: true },
      },
    }))
  }

  const recordAnswer = (
    moduleId: ModuleId,
    questionId: string,
    isCorrect: boolean,
    confidence: number,
    userChoice: number,
  ) => {
    setState((prev) => {
      const question = ALL_QUESTIONS_CORE.find((q) => q.id === questionId)
      const difficulty = question?.difficulty

      const currentQuestionState = prev.questionStates[questionId]
      const newQuestionState = updateQuestionState(
        currentQuestionState,
        questionId,
        isCorrect,
        confidence,
        difficulty,
      )

      const newQuestionStates = {
        ...prev.questionStates,
        [questionId]: newQuestionState,
      }

      const masteryPercentage = calculateMasteryPercentage(
        moduleId,
        newQuestionStates,
      )

      const moduleStats = getModuleStats(moduleId, newQuestionStates)

      const currentModule = prev.progress[moduleId]
      const newRecentQuestions = [
        questionId,
        ...currentModule.recentQuestions.filter((id) => id !== questionId),
      ].slice(0, 15)

      const newStatus = masteryPercentage === 100 ? 'mastered' : 'learning'

      // XP CALCULATION with Level 3 bonus
      let xpGain = 0
      if (isCorrect) {
        if (difficulty === 3) {
          xpGain = confidence >= 3 ? 25 : 15
        } else {
          xpGain = 20
        }
      } else {
        xpGain = 5
      }

      const newXP = prev.stats.xp + xpGain
      const oldLevel = prev.stats.level
      const newLevel = Math.floor(newXP / XP_PER_LEVEL) + 1

      // NIEUW: Detecteer level up maar toon niet direct
      const pendingLevelUp =
        newLevel > oldLevel ? newLevel : prev.pendingLevelUp

      const newErrorLog = [...prev.errorLog]
      if (!isCorrect) {
        newErrorLog.push({
          questionId,
          errorType: 'investigating',
          userChoice,
          confidenceScore: confidence,
          timestamp: Date.now(),
        })
      }

      console.log('📊 Answer recorded:', {
        questionId,
        difficulty,
        isCorrect,
        confidence,
        newStatus: newQuestionState.status,
        xpGained: xpGain,
        levelUp: newLevel > oldLevel ? `${oldLevel} → ${newLevel}` : 'none',
      })

      return {
        ...prev,
        stats: {
          ...prev.stats,
          xp: newXP,
          level: newLevel,
          confidenceIndex: 0,
        },
        progress: {
          ...prev.progress,
          [moduleId]: {
            ...currentModule,
            masteryPercentage,
            status: newStatus,
            recentQuestions: newRecentQuestions,
            reviewNeededCount: moduleStats.reviewNeeded,
            uncertainCount: 0,
            overconfidentCount: moduleStats.overconfidentCount,
            questionsAnswered: [],
          },
        },
        questionStates: newQuestionStates,
        recentConfidenceScores: [],
        errorLog: newErrorLog,
        pendingLevelUp,
      }
    })
  }

  // NIEUW: Clear pending level up (called after showing modal)
  const clearPendingLevelUp = () => {
    setState((prev) => ({
      ...prev,
      pendingLevelUp: undefined,
    }))
  }

  // DEV TOOLS: Jump to level
  const devJumpToLevel = (targetLevel: number) => {
    setState((prev) => {
      const xpNeeded = (targetLevel - 1) * XP_PER_LEVEL
      return {
        ...prev,
        stats: {
          ...prev.stats,
          xp: xpNeeded,
          level: targetLevel,
        },
      }
    })
    console.log(`🚀 DEV: Jumped to Level ${targetLevel}`)
  }

  // DEV TOOLS: Reset progress
  const devResetProgress = () => {
    localStorage.removeItem(STORAGE_KEY)
    setState(INITIAL_STATE)
    console.log('🔄 DEV: Progress reset')
  }

  return {
    state,
    markCheatSheetViewed,
    recordAnswer,
    clearPendingLevelUp,
    devJumpToLevel,
    devResetProgress,
  }
}
